import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  Stack
} from '@mui/material';
import { Search, AutoAwesome, TrendingUp, Cancel, RestartAlt } from '@mui/icons-material';

const smuOptions = ['AM1', 'AM2', 'EU', 'APMEA'];
const assignmentOptions = ['Modernization', 'Migration', 'CloudOps'];
const sectorOptions = ['BFSI', 'FSI', 'Banking', 'Financial Services', 'Insurance', 'RCT', 'Retail', 'Consumer', 'Travels Transportation', 'E&U', 'Energy', 'Utility', 'Government', 'PSU', 'Automotive', 'Manufacturing', 'Telecommunication', 'Technology', 'Human Resource', 'HMD', 'Healthcare', 'Medical Devices'];

const DocumentSearch = ({ onSearch, onReset }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const addTag = (tag, currentTags = selectedTags) => {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed && !currentTags.includes(trimmed)) {
      return [...currentTags, trimmed];
    }
    return currentTags;
  };

  const commitInput = () => {
    if (inputValue.trim()) {
      const parts = inputValue.split(',');
      let newTags = [...selectedTags];
      parts.forEach(p => { newTags = addTag(p, newTags); });
      setSelectedTags(newTags);
      setInputValue('');
      return newTags;
    }
    return selectedTags;
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.endsWith(',')) {
      const tag = val.slice(0, -1).trim().toLowerCase();
      if (tag && !selectedTags.includes(tag)) {
        const newTags = [...selectedTags, tag];
        setSelectedTags(newTags);
        handleSearch(newTags);
      }
      setInputValue('');
    } else {
      setInputValue(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newTags = commitInput();
      handleSearch(newTags);
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      const newTags = selectedTags.slice(0, -1);
      setSelectedTags(newTags);
      handleSearch(newTags);
    }
  };

  const handleSearch = async (tags = selectedTags) => {
    if (onSearch) {
      setIsSearching(true);
      await onSearch(tags.join(','));
      setTimeout(() => setIsSearching(false), 500);
    }
  };

  const handleChipClick = (tag) => {
    const normalized = tag.trim().toLowerCase();
    if (selectedTags.includes(normalized)) {
      const newTags = selectedTags.filter(t => t !== normalized);
      setSelectedTags(newTags);
      handleSearch(newTags);
    } else {
      const newTags = [...selectedTags, normalized];
      setSelectedTags(newTags);
      handleSearch(newTags);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = selectedTags.filter(t => t !== tagToRemove);
    setSelectedTags(newTags);
    handleSearch(newTags);
  };

  const handleClearAll = () => {
    setSelectedTags([]);
    handleSearch([]);
  };

  const handleReset = () => {
    setSelectedTags([]);
    setInputValue('');
    if (onReset) onReset();
  };

  const isChipActive = (tag) => selectedTags.includes(tag.trim().toLowerCase());

  const chipStyle = (isActive) => ({
    backgroundColor: isActive ? 'rgba(0, 172, 193, 0.5)' : 'rgba(255,255,255,0.15)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '12px',
    borderRadius: '12px',
    backdropFilter: 'blur(10px)',
    border: isActive ? '2px solid rgba(0, 172, 193, 0.8)' : '1px solid rgba(255,255,255,0.25)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 172, 193, 0.4)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
    }
  });

  return (
    <Paper 
      elevation={0}
      sx={{ 
        background: 'linear-gradient(135deg, #3B1F5B 0%, #A333C8 100%)',
        borderRadius: '24px',
        p: 4,
        mb: 4,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          zIndex: 0
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AutoAwesome sx={{ color: '#fff', mr: 2, fontSize: 32 }} />
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#fff',
              fontWeight: 700,
              flexGrow: 1,
              background: 'linear-gradient(45deg, #fff 30%, #E0E0E0 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Case Studies Search
          </Typography>
          <Button
            variant="contained"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            sx={{
              minWidth: 120,
              borderRadius: '16px',
              background: 'linear-gradient(45deg, #000000 30%, #3B1F5B 90%)',
              color: '#fff !important',
              fontWeight: 600,
              fontSize: '15px',
              textTransform: 'none',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255,255,255,0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            Reset
          </Button>
        </Box>

        {/* Search input with bubbles */}
        <Box sx={{ 
          mb: 3, 
          minHeight: 64,
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          px: 2,
          py: 1,
          border: '2px solid rgba(163, 51, 200, 0.3)'
        }}>
          <Search sx={{ color: 'rgba(163, 51, 200, 0.7)', mr: 1 }} />
          {selectedTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveTag(tag)}
              deleteIcon={<Cancel sx={{ fontSize: 18 }} />}
              sx={{
                background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
                color: '#fff',
                fontWeight: 600,
                borderRadius: '12px',
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: '#fff' }
                }
              }}
            />
          ))}
          <input
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={selectedTags.length === 0 ? 'Type tags separated by comma...' : 'Add more tags...'}
            style={{
              flex: 1,
              minWidth: 150,
              border: 'none',
              outline: 'none',
              fontSize: '16px',
              fontWeight: 500,
              color: '#1E1E1E',
              background: 'transparent',
              fontFamily: 'inherit'
            }}
          />
          {selectedTags.length > 0 && (
            <Button
              size="small"
              onClick={handleClearAll}
              sx={{ 
                textTransform: 'none', 
                color: '#A333C8', 
                fontWeight: 600,
                minWidth: 'auto'
              }}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => { const newTags = commitInput(); handleSearch(newTags); }}
            disabled={isSearching || (selectedTags.length === 0 && !inputValue.trim())}
            sx={{
              minWidth: 100,
              height: 40,
              borderRadius: '12px',
              background: isSearching 
                ? 'linear-gradient(45deg, #C2185B 30%, #880E4F 90%)'
                : 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              color: '#fff !important',
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 25px rgba(0,0,0,0.2)'
              },
              '&.Mui-disabled': {
                color: '#fff !important',
                background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
                opacity: 0.7
              }
            }}
          >
            {isSearching ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp sx={{ fontSize: 16, animation: 'spin 1s linear infinite' }} />
                Searching
              </Box>
            ) : (
              'Search'
            )}
          </Button>
        </Box>

        {/* SMU Tags */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, ml: 0.5 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '16px', minWidth: 160, whiteSpace: 'nowrap' }}>
            SMU :
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {smuOptions.map((opt) => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => handleChipClick(opt)}
                sx={chipStyle(isChipActive(opt))}
              />
            ))}
          </Stack>
        </Box>

        {/* Assignment Type Tags */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, ml: 0.5 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '16px', minWidth: 160, whiteSpace: 'nowrap' }}>
            Assignment Type :
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {assignmentOptions.map((opt) => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => handleChipClick(opt)}
                sx={chipStyle(isChipActive(opt))}
              />
            ))}
          </Stack>
        </Box>

        {/* Sector Tags */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, ml: 0.5 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '16px', minWidth: 160, whiteSpace: 'nowrap', pt: 0.5 }}>
            Sector :
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {sectorOptions.map((opt) => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => handleChipClick(opt)}
                sx={chipStyle(isChipActive(opt))}
              />
            ))}
          </Stack>
        </Box>
      </Box>
      
      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Paper>
  );
};

export default DocumentSearch;
